import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import { ticketService } from 'src/services/api/ticket.service'
import { useAccountStore } from 'src/stores/account'
import { useCompaniesDirectory } from 'src/composables/account/query/useCompaniesDirectory'
import { CACHE_ACTIONS } from 'src/services/consts/app.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { formatWebsiteUrl } from 'src/services/app/util.service'
import { resolveCustomWebsites } from 'src/services/helpers/ticket.helper'
import { broadcastTicket } from 'src/composables/app/system/useBroadcastChannel'
import { refetchEnabledWhenVisible } from 'src/composables/app/system/useOnlineStatus'
import { isPracticeId } from 'src/composables/onboarding/practice/practiceMode'
import { getPracticeTicket } from 'src/composables/onboarding/practice/practiceSource'

export async function ticketQueryFn({ queryKey, queryClient }) {
  const accountStore = useAccountStore()

  const [, ticketIdFromKey] = queryKey
  const ticketId = ticketIdFromKey?.value ?? ticketIdFromKey

  if (!ticketId) return null
  // Before the service call, the broadcast, and the account write.
  if (isPracticeId(ticketId)) return getPracticeTicket(ticketId)

  const ticket = await ticketService.getTicketById(ticketId)

  if (ticket) {
    broadcastTicket(ticketId, ticket)
    accountStore.updateAccountTickets(CACHE_ACTIONS.UPDATE, ticket)

    if (queryClient && ticket.userMessages) {
      const messagesQueryKey = [QUERY_KEYS.MESSAGES, QUERY_KEYS.TICKET, ticketId]
      queryClient.setQueryData(messagesQueryKey, ticket.userMessages)
    }
  }
  return ticket
}

export function useTicketQuery(ticketIdRef) {
  const accountStore = useAccountStore()
  const companyDictionary = useCompaniesDirectory()
  const queryClient = useQueryClient()

  const enableQuery = computed(() => !!ticketIdRef?.value && !!accountStore.accountId)

  const ticketQuery = useQuery({
    queryKey: [QUERY_KEYS.TICKET, ticketIdRef],
    enabled: enableQuery,
    refetchOnWindowFocus: enableQuery,
    refetchOnReconnect: refetchEnabledWhenVisible(enableQuery),
    queryFn: (context) => ticketQueryFn({ ...context, queryClient }),
  })

  const isTicketLoading = computed(
    () => ticketQuery.isLoading.value || !accountStore.accountId || accountStore.isLoading
  )
  const ticket = computed(() => {
    const rawTicket = ticketQuery.data.value
    if (!rawTicket) return null

    const { isCustomCompany, companyWebsite, productWebsite } = resolveCustomWebsites(rawTicket)
    const website = isCustomCompany
      ? companyWebsite
      : companyDictionary.getWebsite(rawTicket.companyId)

    return {
      ...rawTicket,
      website: formatWebsiteUrl(website),
      companyWebsite,
      productWebsite: formatWebsiteUrl(productWebsite),
      companyName: companyDictionary.resolveCompanyName({
        companyId: rawTicket.companyId,
        fallbackName: rawTicket.companyName,
      }),
      productName: companyDictionary.resolveProductName({
        companyId: rawTicket.companyId,
        productId: rawTicket.productId,
        fallbackName: rawTicket.productName,
      }),
    }
  })

  return {
    ticket,
    isTicketLoading,
    isTicketRefetching: ticketQuery.isRefetching,
    ticketError: ticketQuery.error,
    refetchTicket: ticketQuery.refetch,
  }
}
